import React, {
  forwardRef,
  InputHTMLAttributes,
  TextareaHTMLAttributes
} from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface SharedTextInputProps {
  label?: string | null
  icon?: IconProp
  isValid?: boolean
  showValidationIcon?: boolean
  shouldValidate?: boolean
  showValidationText?: boolean
  validationTextContainerClassName?: string
  validationText?: string
  actionIcon?: IconProp
  showActionIcon?: boolean
  tip?: string | null
  tipContainerClassName?: string
}

type RegularInputProps = InputHTMLAttributes<HTMLInputElement> & {
  type?: Exclude<React.HTMLInputTypeAttribute, 'textarea'>
}

type TextareaInputProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  type: 'textarea'
}

type TextInputProps = SharedTextInputProps &
  (RegularInputProps | TextareaInputProps)

const TextInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextInputProps
>(
  (
    {
      label,
      type,
      shouldValidate = true,
      icon,
      isValid = true,
      showValidationIcon = true,
      showValidationText = false,
      validationText = 'Ongeldige invoer',
      validationTextContainerClassName = '',
      className = '',
      actionIcon,
      showActionIcon = false,
      tip = null,
      tipContainerClassName = '',
      ...nativeProps
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false)

    const hasValidationError = !isValid && shouldValidate

    const inputClassName = `
      textfield
      ${hasValidationError ? 'border-border-danger focus:border-border-danger' : ''}
      ${icon ? 'pl-10' : ''}
      ${className}
      ${className.includes('mt') ? '' : 'mt-4'}
      ${className.includes('mb') ? '' : 'mb-4'}
    `

    const field =
      type === 'textarea'
        ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            className={`${inputClassName} p-2`}
            {...(nativeProps as TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        )
        : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            className={inputClassName}
            type={type === 'password' && showPassword ? 'text' : type}
            {...(nativeProps as InputHTMLAttributes<HTMLInputElement>)}
          />
        )

    return (
      <>
        {label && <label className='label'>{label}</label>}

        <div className='relative w-full'>
          {icon && (
            <FontAwesomeIcon
              icon={icon}
              className='text-icon-secondary absolute left-4 top-1/2 -translate-y-1/2'
            />
          )}

          {field}

          {type === 'password' && (
            <FontAwesomeIcon
              icon={showPassword ? 'eye-slash' : 'eye'}
              className='text-icon-secondary absolute right-12 top-[40px] -translate-y-1/2 cursor-pointer'
              onClick={() => setShowPassword((current) => !current)}
            />
          )}

          {hasValidationError && showValidationIcon && (
            <FontAwesomeIcon
              icon='exclamation-circle'
              className='text-status-danger-foreground absolute right-4 top-[40px] -translate-y-1/2'
            />
          )}

          {actionIcon && showActionIcon && (
            <FontAwesomeIcon
              icon={actionIcon}
              className='text-status-success-foreground absolute right-4 top-1/2 -translate-y-1/2'
            />
          )}
        </div>

        {hasValidationError && showValidationText && (
          <div
            className={`relative text-xs text-status-danger-foreground ${validationTextContainerClassName}`}
          >
            {validationText}
          </div>
        )}

        {tip && (
          <div
            className={`mt-1 text-xs font-semibold text-text-muted ${tipContainerClassName}`}
          >
            {tip}
          </div>
        )}
      </>
    )
  }
)

TextInput.displayName = 'TextInput'

export default TextInput
